// ./src/components/features/Dashboard/DashboardAlerts/DashboardAlerts.tsx
import React, { useCallback } from "react";
import { Paper } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useUserNotificationsContext } from "@/contexts/UserNotificationsContext";

import { UserNotification } from "@/utils/api/UserNotificationService";

import { AlertList } from "@/components/features/Alerts";

// ============================================
// Types
// ============================================

export interface DashboardAlertsProps {
  /** Maximum items to display */
  maxItems?: number;

  /** Custom class name */
  className?: string;
}

// ============================================
// Component
// ============================================

/**
 * Dashboard section for displaying notifications/alerts.
 * Uses UserNotificationsContext for data and actions.
 */
export const DashboardAlerts: React.FC<DashboardAlertsProps> = ({
  maxItems = 5,
  className,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifications, loading, markAsRead, markAllAsRead } =
    useUserNotificationsContext();

  const handleAlertClick = useCallback(
    (notification: UserNotification) => {
      // Mark as read when clicked
      if (notification.id) {
        markAsRead(notification.id);
      }

      // Navigate to notification detail
      if (notification.id) {
        navigate(`/notifications/${notification.id}`);
      }
    },
    [navigate, markAsRead]
  );

  const handleViewAll = useCallback(() => {
    navigate("/notifications");
  }, [navigate]);

  const handleMarkAllRead = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  if (!loading && notifications.length == 0) {
    return null;
  }

  return (
    <Paper p="md" withBorder className={className}>
      <AlertList
        notifications={notifications}
        loading={loading}
        maxItems={maxItems}
        onAlertClick={handleAlertClick}
        onViewAll={handleViewAll}
        onMarkAllRead={handleMarkAllRead}
        showTimestamps
        size="md"
        title={t("dashboard.notifications")}
        showHeader
        emptyMessage={t("dashboard.notificationsEmpty")}
        emptyIcon="BellOff"
      />
    </Paper>
  );
};
